import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { StudentService } from '../../services/student.service';
import { MatTable } from '@angular/material/table';
import { FormGroup, FormBuilder } from '@angular/forms';
import { StudentProfileResponse } from '../../api/client-student.service';
import * as moment from 'moment';
import { studentStatuses } from 'app/shared/constants/student-constants.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CacheService } from '../../services/cache.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { StudentEditComponent } from '../student-edit/student-edit.component';
import { StudentEmailEditComponent } from '../student-edit/student-email-edit/student-email-edit.component';
import { filter } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthenticationService } from '../../services/student-authentication.service';

export interface StudentData {
    exitYear: string;
    issueDate: string;
    qcaaData: string;
    atarData: string;
    atarScore: number | string;
}

@Component({
    selector: 'app-student',
    templateUrl: './student.component.html',
    styleUrls: ['./student.component.scss'],
})
export class StudentComponent implements OnInit {
    @ViewChild(MatTable) table: MatTable<any>;
    @ViewChild('noMatchDialog') noMatchDialog: TemplateRef<any>;

    student: StudentProfileResponse;
    shareDetailsForm: FormGroup;
    displayedColumnsTable1: string[] = [
        'exitYear',
        'dateOfIssue',
        'atarData',
    ];
    displayedColumnsTable2: string[] = ['exitYear', 'qcaaData', 'atarData'];
    studentDataDetails: StudentData[] = [];
    showStudentUpdate: boolean;
    showInfo: boolean = false;

    editEmailDialogRef: MatDialogRef<StudentEmailEditComponent>;

    constructor(
        private studentService: StudentService,
        private authenticationService: AuthenticationService,
        private formBuilder: FormBuilder,
        private _snackBar: MatSnackBar,
        private _cacheService: CacheService,
        public dialog: MatDialog,
        private router: Router
    ) {}

    async setShowStudentUpdate($event) {
        this.student = await this.studentService.student({ url: 'student', cacheMins: 1 });

        this.showStudentUpdate = $event;

        this.initiateStudentData();
        this.shareDetailsForm = this.formBuilder.group({
            optIn: [!this.student.personalDetails.shareDataWithSchool],
            sendEmail: [this.student.personalDetails.receiveResultsByEmail]
        });

        if (!this.isMatchedToQCAAStudent()) {
            this.showNoMatchDialog();
        }
    }

    async ngOnInit() {
        this.student = await this.studentService.student({ url: 'student', cacheMins: 1 });

        if (!this.studentRegistrationComplete()) {
            this._cacheService.remove('student');
            this.showStudentUpdate = true;
        }

        this.initiateStudentData();
        this.shareDetailsForm = this.formBuilder.group({
            optIn: [!this.student.personalDetails.shareDataWithSchool],
            sendEmail: [this.student.personalDetails.receiveResultsByEmail],
        });

        if (this.studentRegistrationComplete()) {
            if (!this.isMatchedToQCAAStudent()) {
                this.showNoMatchDialog();
            }
        }
    }

    async shareDetailsChanged() {
        try {
            this._cacheService.remove('student');
            await this.studentService.studentShareDataUpdate(!this.shareDetailsForm.value.optIn);
        } catch (err) {
            this._snackBar.open(
                'Something went wrong. Please wait a few minutes then try again',
                'Close',
                {
                    duration: 8000,
                    panelClass: ['fail-snackbar'],
                }
            );
        }
    }

    async sendEmailChanged() {
        try {
            this._cacheService.remove('student');
            await this.studentService.studentSendEmailUpdate(this.shareDetailsForm.value.sendEmail);

        } catch (err) {
            this._snackBar.open(
                'Something went wrong. Please wait a few minutes then try again',
                'Close',
                {
                    duration: 8000,
                    panelClass: ['fail-snackbar'],
                }
            );
        }
    }

    editDetails(): void {
        this.dialog.open(StudentEditComponent, {
          width: '500px',
          data: { student: this.student, matchedToQCAAStudent:  this.isMatchedToQCAAStudent()},
        });
    }

    editEmail(): void {
        this.editEmailDialogRef = this.dialog.open(StudentEmailEditComponent, {
            width: '500px',
            data: { student: this.student},
        });

        this.editEmailDialogRef.afterClosed().pipe(
            filter(emailEdited => emailEdited)
        ).subscribe(() => {
            this.logout();
        });
    }

    async logout() {
        try {
            await this.authenticationService.logout();
            this.router.navigate(['/sessions/signin']);
        } catch (error) {
            this.authenticationService.removeTokens();
            location.reload(true);
        }
    }

    showNoMatchDialog(): void {
        this.dialog.open(this.noMatchDialog, {
            width: '500px'
        });
    }

    studentRegistrationComplete(): boolean {
        return this.student?.personalDetails?.firstName?.length > 0;
    }

    allowEditDetails(): boolean {
        return this.studentDataDetails.length === 0;
    }

    allowEditEmail(): boolean {
        return this.studentRegistrationComplete();
    }

    initiateStudentData() {
      this.showInfo = false;
        this.student.atarScores.forEach((atarScore) => {
            let atarScoreResult;
            let atarScoreData;

            if (atarScore.score === 'ineligible') {
                atarScoreResult = studentStatuses.ineligible;
                atarScoreData = studentStatuses.confirmed;
            } else if (
                parseFloat(atarScore.score) >= 0 &&
                parseFloat(atarScore.score) <= 30
            ) {
                atarScoreResult = studentStatuses.thirtyOrLess;
                atarScoreData = studentStatuses.confirmed;
                this.showInfo = true;
            } else if (parseFloat(atarScore.score) > 30) {
                atarScoreResult = atarScore.score;
                atarScoreData = studentStatuses.confirmed;
                this.showInfo = true;
            } else {
                atarScoreResult = studentStatuses.pendingRelease;
                atarScoreData = studentStatuses.pendingRelease;
            }

            this.studentDataDetails.push({
                exitYear: atarScore.exitYear.toString(),
                issueDate: moment(atarScore.releaseDate).format(
                    'DD/MM/YYYY'
                ),
                qcaaData: studentStatuses.confirmed,
                atarData: atarScoreData,
                atarScore: atarScoreResult,
            });
        });
    }

    isMatchedToQCAAStudent(): boolean {
        return this.studentDataDetails.length !== 0;
    }

    get formControls() {
        return this.shareDetailsForm.controls;
    }

    getFormat(date) {
        return moment(moment(date, 'YYYYMMDD')).format('DD/MM/YYYY');
    }
}
